<template>
    <div>
        <!-- Table Container Card -->
        <b-card no-body class="mb-0" data-test="mas-list-table">
            <b-card-header tag="h1" class="border-bottom">
                <h1 class="mb-0">Lists of Designated Individuals and Entities</h1>
                
                <div class="d-flex align-items-center justify-content-end">
                    <b-button
                        variant="primary"
                        class="btn btn-sm"
                        data-test ="add-a-new-mas-list"
                        @click="add()"
                    >
                        <span class="d-flex align-items-center text-nowrap">
                            Import
                        <vector data-name="add" data-class="no-path-fill ml-.5" />
                        </span>
                    </b-button>
                </div>
            </b-card-header>

            <b-card-body>
                <div class="my-2 filter-and-search">
                    <!-- Table Top -->
                    <b-row>
                        <!-- Per Page -->
                        <b-col
                            cols="12"
                            md="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-start
                                mb-1 mb-md-0
                                show-entries-select-box
                            "
                        >
                            <label class="show">Show</label>
                            <v-select
                                v-model="perPage"
                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                :options="perPageOptions"
                                :clearable="false"
                                class="per-page-selector d-inline-block mx-50"
                            />
                            <label>entries</label>
                        </b-col>

                        <!-- FILTERS -->
                        <b-col class="table-search-input" cols="12" md="6">
                            <b-row class="justify-content-end">
                                <div
                                    class="
                                        d-flex
                                        align-items-center
                                        justify-content-end
                                    "
                                >
                                    <b-form-input
                                        v-model="searchQuery"
                                        class="d-inline-block mr-1"
                                        placeholder="Search..."
                                        data-test="search"
                                    />
                                </div>
                            </b-row>
                        </b-col>
                    </b-row>
                </div>

                <b-table
                    ref="refComplianceTable"
                    class="position-relative"
                    style="font-size: 13.5px"
                    :items="compliancesData"
                    :fields="tableColumns"
                    :current-page="currentPage"
                    :per-page="perPage"
                    responsive
                    primary-key="id"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="isSortDirDesc"
                    busy.sync="isBusy"
                    :filter="searchQuery"
                >
                    <template #cell(index)="data">
                       {{ data.index + 1 }}
                    </template>

                    <template #cell(category)="data">
                        <b-link 
                            :data-test="`viewRelatedDetails-${data.item.id}`"
                            @click.prevent="viewRelatedDetails(data.item)"
                        >
                            {{ data.item.category }}
                            <!-- <img 
                                :src="'/icons/external-link.svg'"
                                width="14px"
                                style="margin-bottom: 3px;" 
                            /> -->
                        </b-link>
                    </template>

                     <template  #cell(regulations)="data">
                        <div>
                            <div v-if="Array.isArray(data.item.regulations)" class="regulations">
                                <div v-for="(regulations, i) in data.item.regulations" :key="regulations" :class="i+1 < data.item.regulations.length ? 'border-bottom dynamic-border' : ''">
                                  <div href="#" style="display:block;">
                                     {{ regulations }} 
                                  </div> 
                                </div>
                            </div>
                            
                            <div v-else>
                                 <div href="#" style="display:block;">
                                    {{ data.item.regulations }}
                                 </div>
                            </div>
                        </div>
                    </template>

                    <!-- Column: Status -->
                    <template #cell(status)="data">
                        <b-badge
                            pill
                            :variant="`light-${resolveMasListStatusVariant(
                                data.item.status
                            )}`"
                            class="text-capitalize"
                        >
                            {{ data.item.status }}
                        </b-badge>
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)= "data">
                        <div class="cursor-pointer">
                            <div>
                                <b-dropdown size="sm" menu-class="" dropright variant="link" toggle-class="text-decoration-none" no-caret>
                                    <template #button-content>
                                        <feather-icon icon="MoreVerticalIcon"/>
                                    </template>

                                    <!-- <b-dropdown-item
                                        :data-test="`view-${data.item.id}`"
                                        @click="view(data.item)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="EyeIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Check Newly added Records</span>
                                        </div>
                                    </b-dropdown-item> -->

                                    <b-dropdown-item
                                        :data-test="`upload-${data.item.id}`"
                                        @click="upload(data.item)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="UploadIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Reupload</span>
                                        </div>
                                    </b-dropdown-item>

                                    <b-dropdown-item
                                        :data-test="`edit-${data.item.id}`"
                                        @click="edit(data.item)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="EditIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Edit</span>
                                        </div>
                                    </b-dropdown-item>
                                    
                                    <b-dropdown-item
                                        :data-test="`delete-${data.item.id}`"
                                        @click="remove(data.item)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="TrashIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Delete</span>
                                        </div>
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>
                        </div>
                    </template>
                </b-table>

                <loader v-if="isLoading" data-type="Table" class="custom-loading" />

                <div class="mx-2 mb-2">
                    <b-row>
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-start
                            "
                        >
                            <span class="text-muted"
                                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                                {{ dataMeta.of }} entries</span
                            >
                        </b-col>
                        <!-- Pagination -->
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-end
                            "
                        >
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="totalCompliance"
                                :per-page="perPage"
                                first-number
                                last-number
                                class="mb-0 mt-1 mt-sm-0"
                                prev-class="prev-item"
                                next-class="next-item"
                            >
                                <template #prev-text>
                                    <feather-icon
                                        icon="ChevronLeftIcon"
                                        size="18"
                                    />
                                </template>
                                <template #next-text>
                                    <feather-icon
                                        icon="ChevronRightIcon"
                                        size="18"
                                    />
                                </template>
                            </b-pagination>
                        </b-col>
                    </b-row>
                </div>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import {
    BRow,
    BCol,
    BLink,
    BCard,
    BBadge,
    BTable,
    BButton,
    BCardBody,
    BDropdown,
    BFormInput,
    BPagination,
    BCardHeader,
    BDropdownItem,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref } from '@vue/composition-api';
import useMasList from './useMasList';

export default {
    components: {
        BRow,
        BCol,
        BLink,
        BCard,
        BTable,
        BBadge,
        vSelect,
        BButton,
        BDropdown,
        BCardBody,
        BFormInput,
        BPagination,
        BCardHeader,
        BDropdownItem,
    },

    props: {
        compliancesData : {
            type: Array,
            required: true,
        },

        isLoading: {
            type: Boolean,
            required: true,
        },
    },

    setup() {
        const filterMode = ref(false);

        const {
            sortBy,
            isBusy,
            perPage,
            dataMeta,
            currentPage,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            perPageOptions,
            totalCompliance,
            refComplianceTable,
            resolveMasListStatusVariant,
        } = useMasList();

        return {
            sortBy,
            perPage,
            dataMeta,
            searchQuery,
            currentPage,
            tableColumns,
            isSortDirDesc,
            perPageOptions,
            refComplianceTable,
            resolveMasListStatusVariant,

            totalCompliance,

            // UI
            isBusy,
            filterMode,
        };
    },
    
    methods:{
        add() {
            this.$emit('add');
        },

        edit(data) {
            this.$emit('edit', data);
        },

        upload(data) {
            this.$emit('upload', data);
        },

        remove(data) {
            this.$emit('remove', data);
        },

        viewRelatedDetails(data) {
            this.$emit('viewRelatedDetails', data);
        }
    }
};
</script>

<style lang="scss" scoped>
.table-responsive {
    overflow: inherit;
}

.regulations div:not(:first-child) {
    padding-top: 8px;
}

.dynamic-border {
  padding-bottom: 8px;
}
</style>